import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { T } from "../theme";
import Banner from "../components/Banner";
import Pill from "../client/Pill";
import { orgName, contactName } from "../engagement";
import { typeLabel, REGIONS } from "../finance/presets";
import useEngagement from "./useEngagement";
import RoadmapEditor from "./RoadmapEditor";
import LedgerTab from "./LedgerTab";
import SetupForm from "./SetupForm";
import InvoicesTab from "./InvoicesTab";
import { fromConfig } from "./configForm";

// One engagement, everything Thread edits about it. Sections live in the
// URL hash so a reload keeps the place. Setup and Invoices share one draft
// of the config (explicit Save); Roadmap and Ledger save as you go.
const SECTIONS = [["setup", "Setup"], ["roadmap", "Roadmap"], ["ledger", "Ledger"], ["invoices", "Invoices"]];
const STATUS_TONE = { "Pre-engagement": "neutral", "Activation": "attention", "In Flight": "live", "Complete": "good" };

export default function Workspace() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const eng = useEngagement(slug);
  const section = SECTIONS.some(([k]) => `#${k}` === location.hash) ? location.hash.slice(1) : "roadmap";

  // The config draft, shared by Setup and Invoices. Reset when a save lands.
  const [draft, setDraft] = useState(null);
  const [savedRaw, setSavedRaw] = useState(null);
  useEffect(() => { if (eng.rawConfig !== savedRaw) { setSavedRaw(eng.rawConfig); setDraft(null); } }, [eng.rawConfig, savedRaw]);
  const dirty = draft !== null;
  useEffect(() => {
    if (!dirty) return undefined;
    const h = e => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
  const form = draft || fromConfig(eng.rawConfig);
  const guardLeave = (e) => { if (dirty && !window.confirm("You have unsaved setup changes. Leave without saving?")) e.preventDefault(); };

  if (eng.loading) return <p style={{ color: T.fgMuted, fontSize: 14, textAlign: "center", marginTop: 40 }}>Loading…</p>;
  if (eng.notFound) return <p style={{ color: T.fgMuted, fontSize: 14, textAlign: "center", marginTop: 40 }}>No engagement with the slug "{slug}". <Link to="/admin/engagements">Back to the list</Link>.</p>;
  const f = eng.engagement.fields;
  const typeLabelText = typeLabel(f["Engagement Type"]);

  return (
    <div className="admin-main" style={{ maxWidth: 960, margin: "28px auto 64px", padding: "0 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, color: T.fgMuted }}><Link to="/admin/engagements" onClick={guardLeave} style={{ color: T.fgMuted, textDecoration: "none" }}>Engagements</Link> / {orgName(f)}</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: T.root, margin: "4px 0 2px" }}>{orgName(f)}</h2>
          <div style={{ fontSize: 13, color: T.fgSecondary, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {typeLabelText && <span>{typeLabelText}</span>}
            <Pill tone="neutral">{REGIONS[eng.cfg.billing?.region]?.label || "New Zealand"} · {eng.cfg.currency}</Pill>
            {f.Status && <Pill tone={STATUS_TONE[f.Status] || "neutral"}>{f.Status}</Pill>}
            {contactName(f) && <span>· {contactName(f)}</span>}
          </div>
        </div>
        <a href={`/client/${f.Slug}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: T.root, background: T.accentSoft, padding: "8px 14px", borderRadius: T.radiusMd, textDecoration: "none" }}>Open client page ↗</a>
      </div>

      {eng.stale && <Banner tone="error" message="This page is out of date, reload to continue. Your last change has not been saved." actionLabel="Reload" onAction={() => window.location.reload()} />}
      {Object.keys(eng.failed).length > 0 && !eng.stale && <Banner tone="error" message="Couldn't save your last change. It is still on screen; nothing has been lost yet." actionLabel="Retry" onAction={eng.retry} />}
      {eng.readOnly.map(field => <Banner key={field} tone="warn" message={`${field} couldn't be read, so this page won't save changes to it. Nothing stored has been changed. Fix the record in Airtable.`} />)}

      <div className="hub-tabs" style={{ display: "flex", gap: 4, borderBottom: `1px solid ${T.borderSubtle}`, marginBottom: 22 }}>
        {SECTIONS.map(([key, label]) => (
          <button key={key} onClick={() => navigate(`#${key}`, { replace: true })} style={{ padding: "10px 18px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", fontFamily: T.fontSans, background: "transparent", color: section === key ? T.root : T.fgMuted, borderBottom: section === key ? `2px solid ${T.root}` : "2px solid transparent", marginBottom: -1 }}>
            {label}{dirty && (key === "setup" || key === "invoices") ? <span style={{ marginLeft: 6, fontSize: 10, color: T.statusRisk }}>● unsaved</span> : null}
          </button>
        ))}
      </div>

      {section === "roadmap" && <RoadmapEditor steps={eng.steps} actions={eng.stepActions} cfg={eng.cfg} />}
      {section === "ledger" && <LedgerTab eng={eng} />}
      {section === "setup" && <SetupForm eng={eng} form={form} setForm={setDraft} dirty={dirty} />}
      {section === "invoices" && <InvoicesTab eng={eng} form={form} setForm={setDraft} dirty={dirty} />}
    </div>
  );
}
